import React, { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Save, Trash2, ArrowLeft } from "lucide-react";
import { useAuth } from "../lib/auth";

const EMPTY_PLAN = { name: "", max_managers: 1, max_employees: 5, monthly_cost: 0, trial_days: 14, review_360_enabled: false };

export default function PlatformPlans() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(EMPTY_PLAN);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/plans");
      setPlans(r.data);
    } catch (e) {
      toast.error(apiError(e, "Failed to load plans"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = {
        name: draft.name.trim(),
        max_managers: Number(draft.max_managers),
        max_employees: Number(draft.max_employees),
        monthly_cost: Number(draft.monthly_cost),
        trial_days: Number(draft.trial_days || 0),
        review_360_enabled: !!draft.review_360_enabled,
      };
      if (!payload.name) { toast.error("Name required"); return; }
      if (editingId) {
        await api.put(`/admin/plans/${editingId}`, payload);
        toast.success("Plan updated");
      } else {
        await api.post("/admin/plans", payload);
        toast.success("Plan created");
      }
      setDraft(EMPTY_PLAN);
      setEditingId(null);
      await load();
    } catch (e) {
      toast.error(apiError(e, "Save failed"));
    }
  };

  const editPlan = (p) => {
    setEditingId(p.id);
    setDraft({
      name: p.name,
      max_managers: p.max_managers,
      max_employees: p.max_employees,
      monthly_cost: p.monthly_cost,
      trial_days: p.trial_days || 0,
      review_360_enabled: !!p.review_360_enabled,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deletePlan = async (id) => {
    if (!window.confirm("Delete this plan?")) return;
    try {
      await api.delete(`/admin/plans/${id}`);
      toast.success("Plan deleted");
      await load();
    } catch (e) {
      toast.error(apiError(e, "Delete failed"));
    }
  };

  if (!user?.is_platform_owner) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-20 text-center" data-testid="platform-plans-forbidden">
        <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00] mb-2">[FORBIDDEN]</div>
        <h1 className="font-display font-bold text-3xl">Platform owner access required</h1>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12" data-testid="platform-plans-page">
      <div className="mb-8">
        <a href="/admin/platform" className="font-mono-cal text-[11px] uppercase tracking-[0.2em] text-[#666] hover:text-[#FF4F00] inline-flex items-center gap-1" data-testid="back-to-platform">
          <ArrowLeft size={12} /> Platform admin
        </a>
      </div>
      <div className="font-mono-cal text-[11px] uppercase tracking-[0.2em] text-[#FF4F00] mb-3">[ GOD MODE ]</div>
      <h1 className="font-display font-bold text-5xl tracking-tight mb-2">Subscription plans</h1>
      <p className="text-[#666] mb-10">Create, edit and delete the plans your organizations can subscribe to.</p>

      {loading ? <div className="font-mono-cal text-xs">[LOADING…]</div> : (
        <section data-testid="plan-editor-section">
          <div className="cal-card p-6 mb-6" data-testid="plan-editor">
            <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-3">{editingId ? "[EDIT PLAN]" : "[NEW PLAN]"}</div>
            <div className="grid md:grid-cols-5 gap-4">
              <div>
                <label className="cal-label">Name</label>
                <input className="cal-input" value={draft.name} onChange={(e) => setDraft({...draft, name: e.target.value})} data-testid="plan-name"/>
              </div>
              <div>
                <label className="cal-label">Max managers</label>
                <input type="number" min="1" className="cal-input" value={draft.max_managers} onChange={(e) => setDraft({...draft, max_managers: e.target.value})} data-testid="plan-max-managers"/>
              </div>
              <div>
                <label className="cal-label">Max employees</label>
                <input type="number" min="1" className="cal-input" value={draft.max_employees} onChange={(e) => setDraft({...draft, max_employees: e.target.value})} data-testid="plan-max-employees"/>
              </div>
              <div>
                <label className="cal-label">A$ / month</label>
                <input type="number" min="0" step="0.01" className="cal-input" value={draft.monthly_cost} onChange={(e) => setDraft({...draft, monthly_cost: e.target.value})} data-testid="plan-cost"/>
              </div>
              <div>
                <label className="cal-label">Trial days</label>
                <input type="number" min="0" className="cal-input" value={draft.trial_days} onChange={(e) => setDraft({...draft, trial_days: e.target.value})} data-testid="plan-trial"/>
              </div>
            </div>
            <label className="mt-4 flex items-center gap-2 cursor-pointer text-sm" data-testid="plan-360-toggle-label">
              <input
                type="checkbox"
                checked={!!draft.review_360_enabled}
                onChange={(e) => setDraft({...draft, review_360_enabled: e.target.checked})}
                className="w-4 h-4 accent-[#FF4F00]"
                data-testid="plan-360-toggle"
              />
              <span className="font-display">Enable <strong>360° Multi-Rater Reviews</strong> for plans on this tier</span>
              <span className="font-mono-cal text-[9px] uppercase tracking-widest text-[#FF4F00]">[BETA]</span>
            </label>
            <div className="mt-4 flex gap-2">
              <button onClick={save} className="cal-btn" data-testid="plan-save-btn">{editingId ? <><Save size={14}/> Save</> : <><Plus size={14}/> Create</>}</button>
              {editingId && <button onClick={() => { setEditingId(null); setDraft(EMPTY_PLAN); }} className="cal-btn cal-btn-ghost">Cancel</button>}
            </div>
          </div>

          <div className="overflow-x-auto cal-card" data-testid="plans-table">
            <table className="w-full text-sm">
              <thead className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666]">
                <tr><th className="text-left p-3">Name</th><th className="text-right p-3">Managers</th><th className="text-right p-3">Employees</th><th className="text-right p-3">A$/mo</th><th className="text-right p-3">Trial</th><th className="text-center p-3">360°</th><th className="p-3"/></tr>
              </thead>
              <tbody>
                {plans.map((p) => (
                  <tr key={p.id} className="border-t border-[#111]/10" data-testid={`plan-row-${p.id}`}>
                    <td className="p-3 font-display font-bold">{p.name}{p.is_default_trial && <span className="ml-2 font-mono-cal text-[10px] text-[#FF4F00]">[DEFAULT]</span>}</td>
                    <td className="p-3 text-right">{p.max_managers}</td>
                    <td className="p-3 text-right">{p.max_employees}</td>
                    <td className="p-3 text-right">A${p.monthly_cost}</td>
                    <td className="p-3 text-right">{p.trial_days || 0}d</td>
                    <td className="p-3 text-center" data-testid={`plan-360-cell-${p.id}`}>
                      {p.review_360_enabled
                        ? <span className="font-mono-cal text-[10px] text-[#00A36C]">ON</span>
                        : <span className="font-mono-cal text-[10px] text-[#999]">—</span>}
                    </td>
                    <td className="p-3 text-right">
                      <button onClick={() => editPlan(p)} className="font-mono-cal text-xs underline mr-3" data-testid={`edit-plan-${p.id}`}>Edit</button>
                      {!p.is_default_trial && <button onClick={() => deletePlan(p.id)} className="text-[#FF4F00]" data-testid={`delete-plan-${p.id}`}><Trash2 size={14}/></button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
